import React from 'react'
import styled from 'styled-components'
import Link from 'next/link'
import { useRevealProps } from '../../MwPage/components/Reveal'
import VideoPoster from '../../MwPage/components/VideoPoster'
import { Star } from '../../MwPage/icons'
import {
  Eyebrow,
  greyBg,
  Hi,
  ink,
  Lede,
  line,
  sans,
  Section,
  SectionHead,
  Small,
  Stack,
  Stars,
  teal,
} from '../../MwPage/styled'
import { videoTestimonials } from '../../content'
import { useFocus } from '../focus'
import { Big, Card, Grid, H2, Kicker, Wrap } from '../styled'

const Result = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  ${Big} {
    font-size: ${(props) => (props.$smallBig ? 24 : 28)}px;
  }

  blockquote {
    margin: 2px 0 0;
    font-size: 15.5px;
    line-height: 1.6;
    color: ${ink};
  }

  figcaption {
    margin-top: auto;
    padding-top: 6px;
    font-size: 13px;
    font-weight: 700;
    color: ${teal};
  }
`

export const VideosTitle = styled.p`
  margin: 0 0 14px;
  font: 700 19px/1.3 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};
`

// the prototype's .vids, grown to fill the column: the five share the width, and swipe once it is too narrow
export const Videos = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  margin: 0 -24px;
  padding: 0 24px 8px;
  scroll-padding-left: 24px;
  -webkit-overflow-scrolling: touch;
`

const Video = styled.figure`
  flex: 1 0 108px;
  min-width: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  scroll-snap-align: start;

  figcaption {
    font-weight: 700;
    font-size: 13px;
    color: ${ink};
  }
`

const Poster = styled.div`
  width: 100%;
  aspect-ratio: 108 / 136;
  border-radius: 16px;
  background: ${greyBg};
  border: 1.5px solid ${line};
`

// a title in place of the section heading, where the testimonials sit under the mentor's bio
const Title = styled.h3`
  margin: 0;
  font: 700 21.5px/1.3 ${sans};
  letter-spacing: -0.015em;
  color: ${ink};
`

const FiveStars = ({ label }) => (
  <Stars aria-label={label}>
    {[0, 1, 2, 3, 4].map((n) => (
      <Star key={n} />
    ))}
  </Stars>
)

const ResultCard = ({ item, starsLabel }) => (
  <Result as="figure" $tint={!item.plain} $smallBig={item.smallBig} {...useRevealProps()}>
    <Big>{item.big}</Big>
    <Kicker>{item.what}</Kicker>
    <FiveStars label={starsLabel} />
    <blockquote>{item.quote}</blockquote>
    <figcaption>{item.name}</figcaption>
  </Result>
)

// a quote on its own, with the subject it is about where the page tags them
const QuoteCard = ({ item, starsLabel }) => (
  <Result as="figure" {...useRevealProps()}>
    {item.tag && <Kicker>{item.tag}</Kicker>}
    <FiveStars label={starsLabel} />
    <blockquote>{item.quote}</blockquote>
    <figcaption>{item.name}</figcaption>
  </Result>
)

export const VideoCard = ({ video, playLabel, onPlay }) => (
  <Video {...useRevealProps()}>
    <VideoPoster as={Poster} src={video.poster} label={playLabel} onPlay={() => onPlay(video)} size={40} />
    <figcaption>{video.name}</figcaption>
  </Video>
)

const CompactTitle = ({ children }) => <Title {...useRevealProps()}>{children}</Title>

const Heading = ({ testimonials }) => (
  <SectionHead>
    <Eyebrow {...useRevealProps()}>{testimonials.eyebrow}</Eyebrow>
    <H2 {...useRevealProps()}>
      {testimonials.headline} <Hi>{testimonials.headlineAccent}</Hi>
    </H2>
    <Lede {...useRevealProps()}>{testimonials.lede}</Lede>
  </SectionHead>
)

// every member video, the page's own list of them by slug, or none
const videosFor = (videos) => {
  if (videos === false) return []
  if (!Array.isArray(videos)) return videoTestimonials
  return videos.map((slug) => videoTestimonials.find((video) => video.slug === slug)).filter(Boolean)
}

/**
 * "What members say": the results, the quotes and the member video stories. A page with a `title` in place of the
 * heading shows them compactly, as a continuation of the mentor section above.
 *
 * @param onPlay  called with a member's video to open the lightbox
 */
const TestimonialsSection = ({ onPlay }) => {
  const { testimonials } = useFocus()
  const compact = !!testimonials.title
  const videos = videosFor(testimonials.videos)

  return (
    <Section $ice={!compact} style={compact ? { paddingTop: 0 } : undefined}>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          {compact ? <CompactTitle>{testimonials.title}</CompactTitle> : <Heading testimonials={testimonials} />}
          {testimonials.results && (
            <Grid $min={210}>
              {testimonials.results.map((item) => (
                <ResultCard key={item.name} item={item} starsLabel={testimonials.starsLabel} />
              ))}
            </Grid>
          )}
          <Grid $min={compact ? 300 : 210}>
            {testimonials.quotes.map((item) => (
              <QuoteCard key={item.name} item={item} starsLabel={testimonials.starsLabel} />
            ))}
          </Grid>
          {videos.length > 0 && (
            <div>
              <VideosTitle>{testimonials.videosTitle}</VideosTitle>
              <Videos>
                {videos.map((video) => (
                  <VideoCard
                    key={video.slug}
                    video={video}
                    playLabel={testimonials.playLabel(video.name)}
                    onPlay={onPlay}
                  />
                ))}
              </Videos>
              {testimonials.swipeHint && <Small style={{ fontWeight: 700 }}>{testimonials.swipeHint}</Small>}
            </div>
          )}
          <Small>
            {testimonials.disclaimer[0]}
            <Link href="/disclaimer">
              <a>{testimonials.disclaimer[1]}</a>
            </Link>
            {testimonials.disclaimer[2]}
          </Small>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default TestimonialsSection
